import { useCallback, useEffect, useState } from 'react'

const readSessionStorageValue = <T,>(key: string, initialValue: T): T => {
  try {
    const raw = window.sessionStorage.getItem(key)
    if (raw === null) return initialValue
    return JSON.parse(raw) as T
  } catch {
    return initialValue
  }
}

export function useSessionStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = useState<T>(() =>
    readSessionStorageValue(key, initialValue)
  )

  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      setStoredValue(prev => {
        const valueToStore = typeof value === 'function' ? (value as (p: T) => T)(prev) : value

        try {
          window.sessionStorage.setItem(key, JSON.stringify(valueToStore))
        } catch {
          // No-op: fail silently if storage is unavailable.
        }

        return valueToStore
      })
    },
    [key]
  )

  useEffect(() => {
    setStoredValue(readSessionStorageValue(key, initialValue))
  }, [key, initialValue])

  return [storedValue, setValue] as const
}
